import { Space, Table, Tooltip } from 'antd';
import { PermissionContext } from 'components/Global/AuthLayout';
import { useContext, useMemo } from 'react';
import { DeleteActionSvg, EditActionSvg } from 'utils/svgs';
import Ellipse from 'components/Global/Ellipse'; // ✅ Import Ellipse
import styles from './index.module.scss';

const { Column } = Table;

type Props = {
	payGroupData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	setEditData: any;
	openDrawerHandler: any;
	setDrawerInfoHandler: any;
	showModal: any;
	isLoading: boolean;
};

const PayGroupTable = (props: Props) => {
	const {
		payGroupData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		openDrawerHandler,
		setDrawerInfoHandler,
		setEditData,
		showModal,
		isLoading,
	} = props;

	const permissionContext = useContext(PermissionContext);
	const isEdit =
		permissionContext.allowedPermissions.includes('Edit_Pay_Group');
	const isDelete =
		permissionContext.allowedPermissions.includes('Delete_Pay_Group');

	// ✅ OPTIMIZED: Memoize edit handler to prevent recreating on every render
	const editDataHandler = useMemo(
		() => (selectedObj: any) => {
			const currency =
				selectedObj.PayGroupCurrency[0]?.companyCurrency?.id || 'N/A';
			const departments =
				selectedObj.PayGroupDepartments?.map(
					(dept: any) => dept.department.id
				) || [];
			const branches =
				selectedObj.PayGroupBranches?.map(
					(branch: any) => branch.branch.id
				) || [];

			// ✅ Note: We no longer fetch full employee list here
			// It will be loaded on-demand when drawer opens
			const name = selectedObj.name;
			const id = selectedObj.id;

			setEditData({
				id,
				name,
				currency,
				departments,
				branches,
				employees: [], // ✅ Empty initially, load on demand
				employeesName: [],
				earningIds: selectedObj.earningIds,
				deductionIds: selectedObj.deductionIds,
			});

			openDrawerHandler();
			setDrawerInfoHandler('Edit Pay Group');
		},
		[setEditData, openDrawerHandler, setDrawerInfoHandler]
	);

	// ✅ OPTIMIZED: Memoize delete handler
	const deleteDataHandler = useMemo(
		() => (userObject: any) => {
			setEditData(userObject);
			showModal();
		},
		[setEditData, showModal]
	);

	// ✅ OPTIMIZED: Memoize columns to prevent recreation
	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				key: 'name',
				className: 'bg-white',
				width: '25%',
			},
			{
				title: 'Currency',
				dataIndex: 'currency',
				key: 'currency',
				width: '15%',
				className: 'bg-white',
				render: (_: any, record: any) =>
					record?.PayGroupCurrency[0]?.companyCurrency?.currency ||
					'-',
			},
			{
				title: 'Department',
				dataIndex: 'departments',
				key: 'department',
				className: 'bg-white',
				width: '20%',
				render: (_: any, record: any) => {
					const departmentList = record.PayGroupDepartments?.map(
						(dept: any) => dept.department?.name
					).filter(Boolean);

					const content =
						departmentList.length > 0
							? departmentList.join(' | ')
							: '-';

					// ✅ NEW: Use Ellipse component
					return (
						<Ellipse
							maxLength={30}
							message={content}
							isTooltip={true}
							tooltipMessage={content}
							key={`dept-${record.id}`}
						/>
					);
				},
			},
			{
				title: 'Branch',
				dataIndex: 'branches',
				key: 'branch',
				className: 'bg-white',
				width: '15%',
				render: (_: any, record: any) => {
					const branchList = record?.PayGroupBranches?.map(
						(branch: any) => branch.branch.name
					).filter(Boolean);

					const content =
						branchList.length > 0 ? branchList.join(' | ') : '-';

					// ✅ NEW: Use Ellipse component
					return (
						<Ellipse
							maxLength={25}
							message={content}
							isTooltip={true}
							tooltipMessage={content}
							key={`branch-${record.id}`}
						/>
					);
				},
			},
			// ✅ NEW: Employee count column instead of full list
			{
				title: 'Employees',
				dataIndex: 'employeeCount',
				key: 'employeeCount',
				className: 'bg-white',
				width: '15%',
				render: (count: number) => count || 0,
			},
			...(isEdit || isDelete
				? [
						{
							title: 'Action',
							key: 'action',
							className: 'bg-white',
							width: '15%',
							render: (_: any, data: any) => (
								<Space size={20}>
									{data.isPayGroupEditable && isEdit ? (
										<div
											className="cursor-pointer flex align-center justify-center"
											onClick={() =>
												editDataHandler(data)
											}
										>
											<EditActionSvg />
										</div>
									) : (
										<Tooltip title="Cannot edit: no permission or payroll is in draft/pending.">
											<div className="cursor-not-allowed flex align-center justify-center">
												<EditActionSvg color="#928F8F" />
											</div>
										</Tooltip>
									)}
									{data.isPayGroupDeletable && isDelete ? (
										<div
											className="cursor-pointer flex align-center justify-center"
											onClick={() =>
												deleteDataHandler(data)
											}
										>
											<DeleteActionSvg />
										</div>
									) : (
										<Tooltip title="Cannot delete: no permission or payroll already generated.">
											<div className="cursor-not-allowed flex align-center justify-center">
												<DeleteActionSvg color="#928F8F" />
											</div>
										</Tooltip>
									)}
								</Space>
							),
						},
				  ]
				: []),
		],
		[isEdit, isDelete, editDataHandler, deleteDataHandler]
	);

	return (
		<div className={styles['dynamic-table']}>
			<Table
				dataSource={payGroupData}
				columns={columns}
				scroll={{ y: 'calc(100vh - 360px)' }}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize: pageSize,
					showSizeChanger: false,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				onChange={tableChangeHandler}
				rowKey={(record: any) => record.id}
				loading={isLoading}
			/>
		</div>
	);
};

export default PayGroupTable;
